import { PageHero } from '@components/common';
import { Target, FileCheck, ArrowRight, Clock, Users, Shield, Sparkles } from 'lucide-react';
import { CALENDLY_URL } from '@constants';

const idealFor = [
  'Organisations starting their AI journey',
  'Companies using AI tools without formal governance',
  'Businesses concerned about AI-related risks',
  'Leadership teams needing AI use policies',
  'Companies with employees using ChatGPT, Copilot, or similar tools',
  'Organisations in regulated industries adopting AI',
];

const deliverables = [
  {
    title: 'AI Governance Policy',
    description:
      'Comprehensive policy covering acceptable use, prohibited activities, and responsibilities',
  },
  {
    title: 'Risk Assessment Framework',
    description: 'Structured approach to evaluating AI risks specific to your context',
  },
  {
    title: 'Vendor Evaluation Criteria',
    description: 'Checklist and process for assessing AI vendors and tools',
  },
  {
    title: 'Employee Training Materials',
    description: 'Ready-to-deliver training content for staff at all levels',
  },
  {
    title: 'Implementation Playbook',
    description: 'Step-by-step guide for rolling out governance controls',
  },
];

const process = [
  {
    step: '01',
    title: 'Current State Review',
    description:
      'Assessment of existing AI usage, tools, and informal practices across the organisation',
    duration: '2-3 days',
  },
  {
    step: '02',
    title: 'Risk Assessment',
    description:
      'Identification of AI-specific risks including data privacy, IP, and regulatory concerns',
    duration: '3-4 days',
  },
  {
    step: '03',
    title: 'Framework Development',
    description: 'Creation of governance policies, procedures, and controls tailored to your needs',
    duration: '1 week',
  },
  {
    step: '04',
    title: 'Training & Rollout',
    description: 'Staff training delivery and implementation support for new controls',
    duration: '3-5 days',
  },
];

const aiRisks = [
  {
    title: 'Data Privacy',
    description: 'Sensitive data being shared with AI tools and stored externally',
  },
  {
    title: 'Intellectual Property',
    description: 'Proprietary code, strategies, or content input into AI systems',
  },
  {
    title: 'Regulatory Compliance',
    description: 'AI use in regulated contexts without proper oversight',
  },
  {
    title: 'Output Reliability',
    description: 'Decisions based on AI-generated content that may be inaccurate',
  },
  {
    title: 'Shadow AI',
    description: 'Unapproved AI tools being used without IT or security knowledge',
  },
  {
    title: 'Vendor Lock-in',
    description: 'Dependency on AI providers without proper contract protections',
  },
];

export default function AiGovernancePage() {
  const packageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'AI Governance Setup',
    description:
      'Establish practical AI governance that enables safe adoption while managing risks',
    url: 'https://thecisonetwork.com/packages/ai-governance',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(packageSchema) }}
      />
      <PageHero
        title="AI Governance Setup"
        subtitle="Establish practical AI governance that enables safe adoption while managing risks"
        eyebrow="Our Packages"
        seoTitle="AI Governance Setup | The CISO Network"
        seoDescription="Practical AI governance framework implementation. Policies, risk assessments, training materials, and implementation support for safe AI adoption."
      />

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Harness AI Responsibly
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  AI tools like ChatGPT, Microsoft Copilot, and specialised AI applications are
                  transforming how businesses operate. But with this transformation comes new risks:
                  data privacy concerns, intellectual property exposure, regulatory uncertainty, and
                  the challenge of shadow AI usage.
                </p>
                <p>
                  Our AI Governance Setup package helps you establish a practical framework that
                  enables your teams to leverage AI safely. We create clear policies, risk
                  assessment processes, and training programmes that balance innovation with
                  protection.
                </p>
                <p>
                  Whether you are just starting with AI or already have tools in use without formal
                  oversight, this package provides the governance foundation you need.
                </p>
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center">
                  <Target className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">At a Glance</h3>
              </div>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium text-foreground">3-4 weeks</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Focus</span>
                  <span className="font-medium text-foreground">Policy & Implementation</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Deliverables</span>
                  <span className="font-medium text-foreground">Framework + Training</span>
                </div>
              </div>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-hero w-full justify-center"
              >
                Discuss This Package <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Who This Is For
            </h2>
          </div>
          <div className="flex justify-center">
            <ul className="space-y-4 inline-block text-left">
              {idealFor.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-muted-foreground">
                  <Users className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* AI Risks */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              AI Risks We Address
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Common risks organisations face when adopting AI without proper governance
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {aiRisks.map((risk, i) => (
              <div key={i} className="bg-background rounded-2xl p-6 border border-border">
                <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
                  <Shield className="h-5 w-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                  {risk.title}
                </h3>
                <p className="text-muted-foreground text-sm">{risk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              What You Receive
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {deliverables.map((d, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-border">
                <div className="h-10 w-10 rounded-xl bg-teal flex items-center justify-center mb-4">
                  <FileCheck className="h-5 w-5 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground mb-2">{d.title}</h3>
                <p className="text-muted-foreground text-sm">{d.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              The Implementation Process
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {process.map((step, i) => (
                <div key={i} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-14 w-14 rounded-xl bg-teal flex items-center justify-center">
                      <span className="text-lg font-bold text-accent-foreground">{step.step}</span>
                    </div>
                  </div>
                  <div className="flex-grow pb-8 border-b border-border last:border-0">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                      <h3 className="text-xl font-heading font-bold text-foreground">
                        {step.title}
                      </h3>
                      <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        {step.duration}
                      </span>
                    </div>
                    <p className="text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EU AI Act Note */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl p-8 border border-border">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="h-6 w-6 text-teal" />
              <h3 className="text-xl font-heading font-bold text-foreground">EU AI Act Ready</h3>
            </div>
            <p className="text-muted-foreground mb-4">
              With the EU AI Act now in force, organisations using AI face new compliance
              obligations. Our governance framework helps you prepare for these requirements,
              including risk classification, documentation, and oversight mechanisms.
            </p>
            <p className="text-muted-foreground">
              Whether you are deploying AI in the EU or simply want to align with emerging global
              standards, our framework provides a solid foundation for compliant AI use.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Govern Your AI Use?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a discovery call to discuss your AI governance needs and confirm this package is
            right for you.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
